import { Link } from 'react-router-dom';
import { X, UserCircle, ShoppingBag, MapPin } from 'lucide-react';
import type { Brand } from '@/data/brands.api';
import type { Category } from '@/data/categories.api';

type UbuyMobileDrawerProps = {
  open: boolean;
  onClose: () => void;
  language: 'mn' | 'en';
  onLanguageToggle: () => void;
  categories: Category[];
  brands: Brand[];
  cartCount: number;
};

export default function UbuyMobileDrawer({
  open,
  onClose,
  language,
  onLanguageToggle,
  categories,
  brands,
  cartCount,
}: UbuyMobileDrawerProps) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] lg:hidden">
      <button
        type="button"
        aria-label={language === 'mn' ? 'Цэс хаах' : 'Close menu'}
        className="absolute inset-0 bg-black/45"
        onClick={onClose}
      />
      <aside className="relative flex h-full w-[300px] max-w-[84vw] flex-col overflow-y-auto bg-white shadow-2xl">
        <div className="flex h-[54px] items-center justify-between border-b border-[var(--ubuy-border-soft)] px-4">
          <img src="/kg-goods-light-.svg" alt="KG Goods" className="h-9 w-auto" />
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-[var(--ubuy-border-soft)]"
            aria-label={language === 'mn' ? 'Цэс хаах' : 'Close menu'}
          >
            <X size={18} />
          </button>
        </div>

        <div className="space-y-6 px-4 py-5">
          <div className="grid grid-cols-2 gap-2">
            <Link to="/login" onClick={onClose} className="flex items-center gap-2 rounded-lg bg-[var(--ubuy-subtle-surface)] px-3 py-3 text-sm font-semibold">
              <UserCircle size={17} />
              {language === 'mn' ? 'Нэвтрэх' : 'Account'}
            </Link>
            <Link to="/cart" onClick={onClose} className="flex items-center gap-2 rounded-lg bg-[var(--ubuy-subtle-surface)] px-3 py-3 text-sm font-semibold">
              <ShoppingBag size={17} />
              {language === 'mn' ? `Сагс ${cartCount}` : `Cart ${cartCount}`}
            </Link>
            <Link to="/cargo-branches" onClick={onClose} className="col-span-2 flex items-center gap-2 rounded-lg bg-[var(--ubuy-accent)] px-3 py-3 text-sm font-bold text-[var(--ubuy-title)]">
              <MapPin size={17} />
              {language === 'mn' ? 'Cargo салбар сонгох flow хэвээр' : 'Cargo branch flow stays'}
            </Link>
          </div>

          <nav className="space-y-1">
            <Link to="/products?sale=true" onClick={onClose} className="block rounded-lg px-3 py-2 text-sm font-semibold hover:bg-[var(--ubuy-subtle-surface)]">
              {language === 'mn' ? 'Өнөөдрийн deal' : "Today's Deals"}
            </Link>
            <Link to="/products?filter=new" onClick={onClose} className="block rounded-lg px-3 py-2 text-sm font-semibold hover:bg-[var(--ubuy-subtle-surface)]">
              {language === 'mn' ? 'Шинэ ирсэн' : 'New Arrivals'}
            </Link>
            <Link to="/products" onClick={onClose} className="block rounded-lg px-3 py-2 text-sm font-semibold hover:bg-[var(--ubuy-subtle-surface)]">
              {language === 'mn' ? 'Бүх бүтээгдэхүүн' : 'All Products'}
            </Link>
          </nav>

          <div>
            <p className="mb-2 text-xs font-bold uppercase tracking-[0.12em] text-[var(--ubuy-muted)]">
              {language === 'mn' ? 'Ангилал' : 'Categories'}
            </p>
            <div className="space-y-1">
              {categories.slice(0, 8).map((category) => (
                <Link
                  key={category.id}
                  to={`/products?category=${category.slug}`}
                  onClick={onClose}
                  className="block rounded-lg px-3 py-2 text-sm text-[var(--ubuy-muted)] hover:bg-[var(--ubuy-subtle-surface)]"
                >
                  {category.name}
                </Link>
              ))}
            </div>
          </div>

          <div>
            <p className="mb-2 text-xs font-bold uppercase tracking-[0.12em] text-[var(--ubuy-muted)]">
              Brands
            </p>
            <div className="space-y-1">
              {brands.slice(0, 8).map((brand) => (
                <Link
                  key={brand.id}
                  to={`/products?brand=${brand.slug}`}
                  onClick={onClose}
                  className="block rounded-lg px-3 py-2 text-sm text-[var(--ubuy-muted)] hover:bg-[var(--ubuy-subtle-surface)]"
                >
                  {brand.name}
                </Link>
              ))}
            </div>
          </div>

          <button
            type="button"
            onClick={onLanguageToggle}
            className="w-full rounded-lg border border-[var(--ubuy-border-soft)] px-3 py-3 text-left text-sm font-semibold"
          >
            {language === 'mn' ? 'English рүү солих' : 'Switch to Mongolian'}
          </button>
        </div>
      </aside>
    </div>
  );
}
